import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link as RouterLink, useNavigate } from '@tanstack/react-router';
import { registerSchema, type RegisterInput } from '@ddp/shared';
import Button from '@carbon/react/es/components/Button';
import Form from '@carbon/react/es/components/Form/Form';
import { Stack } from '@carbon/react/es/components/Stack/Stack';
import TextInput from '@carbon/react/es/components/TextInput';
import PasswordInput from '@carbon/react/es/components/TextInput/PasswordInput';
import { InlineNotification } from '@carbon/react/es/components/Notification/Notification';

import { useAuthStore } from '../auth/store.js';
import { zodResolver } from '../lib/zod-resolver.js';
import { AuthCard } from './AuthCard.js';

export function RegisterPage() {
  const navigate = useNavigate();
  const registerUser = useAuthStore((state) => state.register);
  const [formError, setFormError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RegisterInput>({ resolver: zodResolver(registerSchema) });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await registerUser(values);
      await navigate({ to: '/' });
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Inscription impossible.');
    }
  });

  return (
    <AuthCard
      title="Créer un compte"
      footer={
        <>
          Déjà inscrit ? <RouterLink to="/login">Se connecter</RouterLink>
        </>
      }
    >
      <Form onSubmit={onSubmit}>
        <Stack gap={6}>
          {formError && (
            <InlineNotification
              kind="error"
              title="Échec de l’inscription"
              subtitle={formError}
              lowContrast
              hideCloseButton
            />
          )}
          <TextInput
            id="displayName"
            labelText="Nom affiché"
            invalid={Boolean(errors.displayName)}
            invalidText={errors.displayName?.message}
            {...register('displayName')}
          />
          <TextInput
            id="organizationName"
            labelText="Nom de l’organisation"
            helperText="Vous en serez le propriétaire (OWNER)."
            invalid={Boolean(errors.organizationName)}
            invalidText={errors.organizationName?.message}
            {...register('organizationName')}
          />
          <TextInput
            id="email"
            type="email"
            labelText="Adresse e-mail"
            autoComplete="email"
            invalid={Boolean(errors.email)}
            invalidText={errors.email?.message}
            {...register('email')}
          />
          <PasswordInput
            id="password"
            labelText="Mot de passe"
            autoComplete="new-password"
            helperText="12 caractères minimum, avec majuscule, minuscule et chiffre."
            showPasswordLabel="Afficher le mot de passe"
            hidePasswordLabel="Masquer le mot de passe"
            invalid={Boolean(errors.password)}
            invalidText={errors.password?.message}
            {...register('password')}
          />
          <Button type="submit" disabled={isSubmitting}>
            Créer le compte
          </Button>
        </Stack>
      </Form>
    </AuthCard>
  );
}
